import { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { styled } from '@mui/material'
import { Button, ButtonProps, Dialog, DialogContent, DialogHeader, DialogFooter } from '@carto/meridian-ds/components'

const ConfirmDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingBottom: theme.spacing(2)
}))

export enum ConfirmDialogResult {
  Confirm,
  Cancel
}

export interface ConfirmDialogProps {
  open: boolean
  onResult: (result: ConfirmDialogResult) => void
  title: ReactNode
  children: ReactNode
  confirmLabel?: ReactNode
  cancelLabel?: ReactNode
  confirmButtonProps?: ButtonProps
  alertContent?: ReactNode
  showCancelButton?: boolean
}

export function ConfirmDialog({
  open,
  onResult,
  title,
  cancelLabel,
  confirmLabel,
  confirmButtonProps,
  alertContent,
  children,
  showCancelButton = true
}: ConfirmDialogProps) {
  const intl = useIntl()
  const isErrorButton = confirmButtonProps?.color === 'error'

  const onCancel = () => {
    onResult(ConfirmDialogResult.Cancel)
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Dialog open={open} onClose={onCancel} aria-labelledby="confirm-dialog-title" height="auto">
        <DialogHeader id="confirm-dialog-title" onClose={onCancel} title={title} />
        <ConfirmDialogContent>{children}</ConfirmDialogContent>
        <DialogFooter alertContent={alertContent} alertProps={{ severity: 'warning' }}>
          {showCancelButton && (
            <Button onClick={() => onResult(ConfirmDialogResult.Cancel)} color={isErrorButton ? 'default' : 'primary'}>
              {cancelLabel || intl.formatMessage({ id: 'common.button.cancel' })}
            </Button>
          )}
          <Button variant="contained" onClick={() => onResult(ConfirmDialogResult.Confirm)} {...confirmButtonProps}>
            {confirmLabel || intl.formatMessage({ id: 'common.button.confirm' })}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

export default ConfirmDialog
